/**
 * TransmittedMailContent
 *
 * Displays detailed PostGrid letter/cheque information for transmitted mail packages.
 * Uses PostGrid components for consistent status display and event timeline.
 */

import {
  CheckCircleIcon,
  DocumentTextIcon,
  TruckIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid';
import React from 'react';

import { MailingClassBadge } from '@/components/postgrid/common/MailingClassBadge';
import { StatusBadge } from '@/components/postgrid/common/PostgridStatusBadge';
import { getStatusConfig } from '@/components/postgrid/common/statusConfig';
import { TestModeBanner } from '@/components/postgrid/common/TestModeBanner';
import { EventTimeline } from '@/components/postgrid/events/EventTimeline';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { CustomUserSchema } from '@/services/api/correspondenceService';
import {
  useGetLetterDetailedQuery,
  usePostgridEventsListQuery,
} from '@/services/api/postgridService';

// ============================================================================
// Types
// ============================================================================

export interface TransmittedMailContentProps {
  postgridLetterId: string;
  recipientName: string;
  destinationDisplay: string;
  transmittedAt: string;
  transmittedBy?: CustomUserSchema | null;
  className?: string;
}

// ============================================================================
// Helpers
// ============================================================================

function formatDate(timestamp: string): { date: string; time: string } {
  const d = new Date(timestamp);
  return {
    date: d.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }),
    time: d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    }),
  };
}

function getUserDisplayName(user?: CustomUserSchema | null): string {
  if (!user) return 'System';
  const name = [user.first_name, user.last_name].filter(Boolean).join(' ');
  return name || user.email || 'System';
}

// ============================================================================
// Sub-components
// ============================================================================

interface DetailRowProps {
  label: string;
  value: React.ReactNode;
}

const DetailRow: React.FC<DetailRowProps> = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4 py-2.5 border-b border-zinc-100 last:border-0">
    <span className="shrink-0 text-[12px] text-zinc-500">{label}</span>
    <span className="max-w-[60%] whitespace-normal break-words text-[12px] font-medium text-zinc-800 text-right">
      {value}
    </span>
  </div>
);

// ============================================================================
// Component
// ============================================================================

export const TransmittedMailContent: React.FC<TransmittedMailContentProps> = ({
  postgridLetterId,
  recipientName,
  destinationDisplay,
  transmittedAt,
  transmittedBy,
  className,
}) => {
  const { date, time } = formatDate(transmittedAt);
  const sentByName = getUserDisplayName(transmittedBy);

  // Fetch letter details and events from PostGrid
  const { data: letter, isLoading: isLoadingLetter } =
    useGetLetterDetailedQuery(postgridLetterId, { skip: !postgridLetterId });

  const { data: events = [], isLoading: isLoadingEvents } =
    usePostgridEventsListQuery(
      { objectId: postgridLetterId },
      { skip: !postgridLetterId },
    );

  const statusConfig = letter?.status ? getStatusConfig(letter.status) : null;

  const handleViewPdf = () => {
    if (letter?.final_pdf_url) {
      window.open(letter.final_pdf_url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className={cn('flex flex-col', className)}>
      {/* Success Header with Status */}
      <div className="text-center py-5">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-50 mb-3">
          <CheckCircleSolidIcon className="w-7 h-7 text-emerald-500" />
        </div>
        <h3 className="text-[15px] font-semibold text-zinc-900 mb-1">
          Mailed Successfully
        </h3>
        <p className="text-[12px] text-zinc-500">
          {date} at {time}
        </p>
      </div>

      {/* Status & Actions Card */}
      {!isLoadingLetter && letter && (
        <div className="bg-zinc-50/60 rounded-xl p-4 mb-4">
          {/* Status Row */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <StatusBadge status={letter.status} />
              <TestModeBanner isTest={!letter.live} variant="compact" />
            </div>
            {letter.mailing_class && (
              <MailingClassBadge
                mailingClass={letter.mailing_class}
                size="small"
              />
            )}
          </div>

          {/* Status Description */}
          {statusConfig && (
            <p className="text-[12px] text-zinc-600 mb-3">
              {statusConfig.description}
            </p>
          )}

          {/* PDF View Button */}
          {letter.final_pdf_url && (
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={handleViewPdf}
            >
              <DocumentTextIcon className="w-4 h-4 mr-2" />
              View PDF
            </Button>
          )}
        </div>
      )}

      {/* Delivery Details Card */}
      <div className="bg-zinc-50/60 rounded-xl px-4 py-1 mb-4">
        <DetailRow
          label="Sent via"
          value={
            <span className="inline-flex items-center gap-1.5">
              <TruckIcon className="w-3.5 h-3.5 text-zinc-600" />
              Mail
            </span>
          }
        />
        <DetailRow label="Recipient" value={recipientName} />
        <DetailRow label="Destination" value={destinationDisplay} />
        <DetailRow label="Sent by" value={sentByName} />
        {letter?.tracking_number && (
          <DetailRow
            label="Tracking number"
            value={
              <span className="font-mono text-[11px]">
                {letter.tracking_number}
              </span>
            }
          />
        )}
        {letter?.postgrid_id && (
          <DetailRow
            label="PostGrid ID"
            value={
              <span className="font-mono text-[11px]">
                {letter.postgrid_id}
              </span>
            }
          />
        )}
      </div>

      {/* Delivery Timeline */}
      <div className="bg-zinc-50/60 rounded-xl p-4">
        <h4 className="text-[13px] font-medium text-zinc-700 mb-3 flex items-center gap-2">
          <CheckCircleIcon className="w-4 h-4" />
          Delivery Status
        </h4>
        <EventTimeline
          events={events}
          isLoading={isLoadingEvents}
          emptyMessage="No delivery events yet"
        />
      </div>
    </div>
  );
};
